/**
 * Per-tenant label terminology overrides.
 *
 * The same underlying domain concept (e.g. a "table" — a row in
 * `BranchTables` with a QR code) is named differently for some tenants.
 * Villa Shams runs a beach venue so a "table" is actually a sun bed
 * (English) / شيزلونج (Arabic). Code, DB column names, API field names,
 * and routes all keep the canonical "table" naming — only rendered UI
 * labels change.
 *
 * Lookups are tenant-slug-keyed (lower-cased). Unknown slugs fall back
 * to the default English/Arabic strings. Add a new tenant by inserting
 * one row in `OVERRIDES`; no app-side changes needed.
 *
 * Used by all three frontends (admin, cashier, menu) — kept here in
 * `packages/utils` so the dictionary stays single-source-of-truth.
 */

export interface TermPair {
  en: string;
  ar: string;
}

/** All terms a tenant can override. Add new keys here as new domain
 *  concepts get tenant-specific labels. */
export interface TenantTerminology {
  /** Singular: "Table" / "طاولة" → "Sun bed" / "شيزلونج" for villashams. */
  table: TermPair;
  /** Plural: "Tables" / "طاولات" → "Sun beds" / "شيزلونجات" for villashams. */
  tables: TermPair;
}

const DEFAULT_TERMINOLOGY: TenantTerminology = {
  table: { en: 'Table', ar: 'طاولة' },
  tables: { en: 'Tables', ar: 'طاولات' },
};

const TENANT_OVERRIDES: Record<string, Partial<TenantTerminology>> = {
  villashams: {
    // Arabic uses the transliterated loanword "صن بيد" (per tenant request),
    // not "شيزلونج". Plural mirrors the English -s with the colloquial
    // Arabic "ز" suffix.
    table: { en: 'Sun bed', ar: 'صن بيد' },
    tables: { en: 'Sun beds', ar: 'صن بيدز' },
  },
  // Staging / test variants of the same venue inherit the same labels —
  // keeping them explicit beats a substring match (which would also catch
  // a hypothetical unrelated tenant whose slug happens to contain the
  // substring "villashams").
  villashams_test: {
    table: { en: 'Sun bed', ar: 'صن بيد' },
    tables: { en: 'Sun beds', ar: 'صن بيدز' },
  },
};

/**
 * Returns the terminology dictionary for the given tenant slug, with the
 * default `{ en, ar }` strings filling in any keys the override omits.
 *
 * Pass the slug as it appears in the JWT (admin/cashier) or in the menu
 * app's URL — comparison is case-insensitive.
 */
export function getTenantTerminology(slug: string | null | undefined): TenantTerminology {
  if (!slug) return DEFAULT_TERMINOLOGY;
  const override = TENANT_OVERRIDES[slug.toLowerCase()];
  if (!override) return DEFAULT_TERMINOLOGY;
  return { ...DEFAULT_TERMINOLOGY, ...override };
}

/**
 * Convenience: read one term directly. Equivalent to
 * `getTenantTerminology(slug)[key]`.
 */
export function getTerm<K extends keyof TenantTerminology>(
  slug: string | null | undefined,
  key: K,
): TermPair {
  return getTenantTerminology(slug)[key];
}
